import cloneDeep from 'lodash.clonedeep';
import { apiGet, apiPost } from './api';

const getState = () => ({
  agent: false,
  choice: 0,
  companyName: '',
  companies: [],
  currentProperty: {},
  currentSigner: {},
  dashboard: true,
  exceptions: '',
  identification: false,
  isAuthorized: false,
  responseOffer: {},
  responses: [],
  sessionUser: {},
  signerRole: 'seller',
  theme: 'light',
  signers: [],
  taxDetails: {},
  userDetails: {},
  selectedGroupCompanies: [],
  agentProfit: [],
  monthlyProfit: [],
  loadingCharts: false,
  loadingAgentProfit: false,
  chartYear: new Date().getFullYear(),
});

const getMutations = () => ({
  setGroupCompanies(state, payload) {
    this._vm.$set(state, 'selectedGroupCompanies', payload);
  },

  setLoadingCharts(state, payload) {
    this._vm.$set(state, 'loadingCharts', payload);
  },

  setLoadingAgentProfit(state, payload) {
    this._vm.$set(state, 'loadingAgentProfit', payload);
  },

  setChartYear(state, payload) {
    this._vm.$set(state, 'chartYear', payload);
  },

  setMonthlyProfit(state, payload) {
    this._vm.$set(state, 'monthlyProfit', payload);
  },

  setAgentProfit(state, payload) {
    this._vm.$set(state, 'agentProfit', payload);
  },

  setChartTheme(state) {
    const theme = state.theme === 'light' ? 'dark' : 'light';
    this._vm.$set(state, 'theme', theme);
  },

  setCompanies(state, companies) {
    this._vm.$set(state, 'companies', companies);
  },

  setDashboardState(state, payload) {
    this._vm.$set(state, 'dashboard', payload);
  },

  setAuthorization(state, payload) {
    // state.isAuthorized = payload;
    this._vm.$set(state, 'isAuthorized', payload);
  },
  setAgent(state, payload) {
    this._vm.$set(state, 'agent', payload);
  },
  setIdentification(state, payload) {
    this._vm.$set(state, 'identification', payload);
  },
  setUserDetails(state, payload) {
    this._vm.$set(state, 'userDetails', payload);
  },
  setSession(state, payload) {
    this._vm.$set(state, 'sessionUser', { ssn: payload.sign_ssn, name: payload.sign_name });
  },
  setCurrentProperty(state, payload) {
    this._vm.$set(state, 'currentProperty', payload.details || {});
    this._vm.$set(state, 'taxDetails', payload.taxDetails || {});
  },
  setAssetDetails(state, payload) {
    this._vm.$set(state.taxDetails, 'assetDetails', payload);
  },
  setSigners(state, payload) {
    this._vm.$set(state, 'signers', payload);
  },
  setSignerRole(state, payload) {
    this._vm.$set(state, 'signerRole', payload);
  },
  setExceptions(state, payload) {
    this._vm.$set(state, 'exceptions', payload);
  },
  setOffererTaxStatus(state, payload) {
    this._vm.$set(state.taxDetails
      .offerers.find(o => o.tarjous_henkilo_id
          === payload.offerer.tarjous_henkilo_id), 'taxStatus', payload.status);
  },
  updateCustomer(state, payload) {
    const offerer = state.taxDetails
      .offerers.find(o => o.henkilo_id
          === payload.henkilo_id);
    if (offerer) {
      this._vm.$set(offerer, 'nimi', payload.nimi);
      this._vm.$set(offerer, 'sotu', payload.sotu);
      this._vm.$set(offerer, 'email', payload.email);
      this._vm.$set(offerer, 'corporateOrIndividual', payload.corporateOrIndividual);
    }
    const seller = state.currentProperty
      .sellers.find(s => s.henkilo_id
        === payload.henkilo_id);
    if (seller) {
      this._vm.$set(seller, 'nimi', payload.nimi);
      this._vm.$set(seller, 'sotu', payload.sotu);
      this._vm.$set(seller, 'email', payload.email);
      this._vm.$set(seller, 'corporateOrIndividual', payload.corporateOrIndividual);
    }
  },
  updateInterestDetail(state, payload) {
    const offerer = state.taxDetails
      .offerers.find(o => o.henkilo_id
          === payload.henkilo_id);
    if (offerer) {
      this._vm.$set(offerer, 'interestDetail', {
        InterestCorporateBuyerIndividualSeller: payload.InterestCorporateBuyerIndividualSeller,
        InterestIndividualBuyerCorporateSeller: payload.InterestIndividualBuyerCorporateSeller,
      });
    }
  },
  setChoice(state, payload) {
    this._vm.$set(state, 'choice', payload);
  },
  setCurrentSigner(state, payload) {
    this._vm.$set(state, 'currentSigner', payload);
  },
  setResponseOffer(state, payload) {
    this._vm.$set(state, 'responseOffer', payload);
  },
  setCompanyName(state, payload) {
    this._vm.$set(state, 'companyName', payload);
  },
  addResponse(state, payload) {
    state.responses.push(payload);
  },
  resetResponses(state) {
    this._vm.$set(state, 'responses', []);
  },
});

const getActions = () => ({
  async getCompanies({ commit }, groupId) { // eslint-disable-line
    const response = await apiGet(`group/${groupId}/companies`);

    if (response.success) {
      commit('setCompanies', response.companies);
    }
  },

  async getAgentProfit({ commit, state }) {
    if (!state.selectedGroupCompanies.length) {
      commit('setAgentProfit', []);
      commit('setLoadingAgentProfit', false);
      return;
    }
    const response = await apiGet(`reporting/agent-profit/${state.chartYear}/${state.selectedGroupCompanies.map(company => company.company_id).join(',')}`);
    const result = response?.result.sort((a, b) => ((a.profit > b.profit) ? 1 : -1)).reverse();

    commit('setAgentProfit', result);
    commit('setLoadingAgentProfit', false);
  },

  async getMonthlyProfit({ commit, state }) {
    commit('setLoadingCharts', true);
    const results = [];
    for await (const company of state.selectedGroupCompanies) {
      let cumulativeProfit = 0;
      const response = await apiGet(`reporting/group-monthly-profit/${state.chartYear}/${company.company_id}`);
      const result = response?.result.map((item) => {
        cumulativeProfit += item.totalProfit;
        return {
          cumulativeProfit: +(cumulativeProfit.toFixed(2)),
          profit: +(item.totalProfit.toFixed(2)),
        };
      });
      results.push({
        data: result,
        name: company.name,
        city: company.city,
        address: company.address,
        phone: company.phone,
        email: company.email,
      });
    }

    commit('setMonthlyProfit', results);
    commit('setLoadingCharts', false);
  },

  hideDashboard({ commit }) {
    commit('setDashboardState', false);
  },

  showDashboard({ commit }) {
    commit('setDashboardState', true);
  },

  async setGroupCompanies({ commit, dispatch }, payload) {
    commit('setGroupCompanies', payload);
    commit('setLoadingAgentProfit', true);
    await dispatch('getMonthlyProfit');
    await dispatch('getAgentProfit');
  },

  async setChartYear({ commit, dispatch }, payload) {
    commit('setChartYear', payload);
    commit('setLoadingAgentProfit', true);
    await dispatch('getMonthlyProfit');
    await dispatch('getAgentProfit');
  },

  setChartTheme({ commit }) {
    commit('setChartTheme');
  },

  async loadAgent({ commit }) {
    const response = await apiGet('agent/getStatus');
    if (response.success) {
      commit('setUserDetails', response.result);
      if (response.success === 'true') {
        commit('setAuthorization', true);
        commit('setAgent', true);
      }
    }
  },
  setCurrentSigner({ commit }, payload) {
    commit('setCurrentSigner', payload);
  },
  setAuthorization({ commit }, payload) {
    commit('setAuthorization', payload);
  },
  setAgent({ commit }, payload) {
    commit('setAgent', payload);
  },
  async setCurrentProperty({ commit }, payload) {
    const response = await apiGet(`properties/details/${payload}`);
    const taxDetails = await apiGet(`properties/taxdetails/${payload}`);
    if (response.success) {
      commit('setCurrentProperty', { details: response.result, taxDetails: taxDetails.result });
    } else {
      commit('setCurrentProperty', {});
    }
  },
  async resetCurrentProperty({ commit }) {
    commit('setCurrentProperty', {});
  },
  async getToken() {
    const code = 'aaa';
    const state = '2019-11-21-07.44.44.45678';
    const nonce = '3965268721';
    const response = await apiGet(`nets/authentication/${code}/${state}/${nonce}`);
    return response;
  },
  setSigners({ commit }, payload) {
    commit('setSigners', payload);
  },
  setSignerRole({ commit }, payload) {
    commit('setSignerRole', payload);
  },
  setExceptions({ commit }, payload) {
    commit('setExceptions', payload);
  },
  setChoice({ commit }, payload) {
    commit('setChoice', payload);
  },
  setResponseOffer({ commit }, payload) {
    commit('setResponseOffer', payload);
  },
  setCompanyName({ commit }, payload) {
    commit('setCompanyName', payload);
  },
  async addResponse(context, payload) {
    const response = cloneDeep(payload);
    await apiPost('properties/offerresponse', { fields: response });
  },
  async sendTaxTransfer({ commit }, payload) {
    const response = await apiPost('properties/taxtransfer', payload);
    const statusPayload = {
      offerer: this.state.taxDetails.offerers
        .find(o => o.tarjous_henkilo_id === payload.updateInfo.offerDetailId),
      status: response.result,
    };
    commit('setOffererTaxStatus', statusPayload);
    return response;
  },
  async modifyTaxTransfer({ commit }, payload) {
    const response = await apiPost(`properties/modifytaxtransfer/${payload.id}`, payload.details);
    commit('setAssetDetails', payload.details);
    return response;
  },

  async modifyAdditionalShares({ commit }, payload) {
    const response = await apiPost(`properties/modifyadditionalshares/${payload.id}`, payload.details);
    commit('setAssetDetails', payload.details);
    return response;
  },

  async updateCustomer({ commit }, payload) {
    const response = await apiPost(`customer/update/${payload.henkilo_id}`, payload);
    commit('updateCustomer', payload);
    return response;
  },

  async updateInterestDetail({ commit }, payload) {
    const response = await apiPost(`customer/updateInterestDetail/${payload.tarjous_henkilo_id}`, payload);
    commit('updateInterestDetail', payload);
    return response;
  },

  async getOfferResponses({ commit }) {
    commit('resetResponses');
    const responses = await apiGet('properties/offerresponses');
    responses.result.forEach((response) => {
      commit('addResponse', { ...JSON.parse(response.fields), signed_date: response.signed_date });
    });
  },
  async getIdentification({ commit }) {
    const response = await apiGet('authorization/identification');
    commit('setIdentification', response.result);
    commit('setCurrentSigner', { ssn: response.sign_ssn, name: response.sign_name, person_id: response.person_id });
    if (response.result === true) {
      commit('setSession', response);
    }
  },
});

const getGetters = () => ({
  isAuthorized: state => state.isAuthorized,
  agent: state => state.agent,
  userDetails: state => state.userDetails,
  choice: state => state.choice,
  responseOffer: state => state.responseOffer,
  exceptions: state => state.exceptions,
  companyName: state => state.companyName,
  responses: state => state.responses,
  signers: state => state.signers,
  signerRole: state => state.signerRole,
});

const storeData = {
  strict: process.env.NODE_ENV === 'development',
  state: getState(),
  mutations: getMutations(),
  actions: getActions(),
  getters: getGetters(),
};

export const getStoreData = () => cloneDeep(storeData); // eslint-disable-line
